import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { toast } from 'react-hot-toast';

export default function EditSubtopicModal({ isOpen, onClose, skill, subtopic }) {
  const [formData, setFormData] = useState({
    name: '',
    status: 'not_started',
    priority: 'medium',
    estimatedHours: '',
    description: '',
    resources: []
  });

  // Initialize form data when modal opens or subtopic changes
  useEffect(() => {
    if (isOpen && subtopic) {
      setFormData({
        name: subtopic.name || '',
        status: subtopic.status || 'not_started',
        priority: subtopic.priority || 'medium',
        estimatedHours: subtopic.estimatedHours || '',
        description: subtopic.description || '',
        resources: Array.isArray(subtopic.resources) ? [...subtopic.resources] : []
      });
    } else {
        // Reset form when closed
        setFormData({
            name: '',
            status: 'not_started',
            priority: 'medium',
            estimatedHours: '',
            description: '',
            resources: []
          });
    }
  }, [isOpen, subtopic]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!skill?.id || !subtopic?.id) {
      toast.error('Cannot save subtopic: Invalid skill or subtopic');
      return;
    }

    try {
      const skillRef = doc(db, 'skills', skill.id);
      
      // Get current skill data to find the correct subtopic index
      const skillDoc = await getDoc(skillRef);
      if (!skillDoc.exists()) {
        toast.error('Skill not found');
        return;
      }

      const currentSkillData = skillDoc.data();
      const subtopicIndex = currentSkillData.subtopics.findIndex(s => s.id === subtopic.id);
      
      if (subtopicIndex === -1) {
        toast.error('Cannot save subtopic: Subtopic not found');
        return;
      }

      // Create a new subtopic object with updated data, preserving original ID and other fields
      const updatedSubtopic = {
        ...currentSkillData.subtopics[subtopicIndex],
        ...formData,
        lastUpdated: new Date().toISOString(),
        resources: formData.resources.filter(resource => resource.title && resource.url), // Filter out empty resources
      };

      // Create a new subtopics array with the updated subtopic
      const updatedSubtopics = [...currentSkillData.subtopics];
      updatedSubtopics[subtopicIndex] = updatedSubtopic;

      // Update the entire skill document with the new subtopics array
      await updateDoc(skillRef, {
        subtopics: updatedSubtopics
      });

      toast.success('Subtopic updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating subtopic:', error);
      toast.error('Failed to update subtopic');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle resource changes
  const handleResourceChange = (index, e) => {
    const { name, value } = e.target;
    const newResources = [...formData.resources];
    newResources[index] = {
      ...newResources[index],
      [name]: value
    };
    setFormData(prev => ({
      ...prev,
      resources: newResources
    }));
  };

  // Add a new resource field
  const handleAddResource = () => {
    setFormData(prev => ({
      ...prev,
      resources: [...prev.resources, { title: '', url: '', type: 'link', description: '' }]
    }));
  };

  // Remove a resource field
  const handleRemoveResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen || !subtopic) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Edit Subtopic: {subtopic.name}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field mt-1"
                  placeholder="Enter subtopic name"
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="input-field mt-1"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="input-field mt-1"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  id="estimatedHours"
                  name="estimatedHours"
                  value={formData.estimatedHours}
                  onChange={handleChange}
                  min="0"
                  step="0.5"
                  className="input-field mt-1"
                  placeholder="Enter estimated hours"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="input-field mt-1"
                  placeholder="Enter subtopic description"
                />
              </div>

            {/* Learning Resources Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Learning Resources</h4>
                <button
                  type="button"
                  onClick={handleAddResource}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  + Add Resource
                </button>
              </div>
              <div className="space-y-3">
                {formData.resources.map((resource, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                    <div className="flex-grow space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Title</label>
                        <input
                          type="text"
                          name="title"
                          value={resource.title}
                          onChange={(e) => handleResourceChange(index, e)}
                          className="input-field text-sm mt-1"
                          placeholder="Resource title"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">URL</label>
                        <input
                          type="url"
                          name="url"
                          value={resource.url}
                          onChange={(e) => handleResourceChange(index, e)}
                          className="input-field text-sm mt-1"
                          placeholder="Resource URL"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Type</label>
                        <select
                          name="type"
                          value={resource.type}
                          onChange={(e) => handleResourceChange(index, e)}
                          className="input-field text-sm mt-1"
                        >
                          <option value="link">Link</option>
                          <option value="video">Video</option>
                          <option value="article">Article</option>
                          <option value="course">Course</option>
                        </select>
                      </div>
                       <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Description (Optional)</label>
                        <textarea
                          name="description"
                          value={resource.description}
                          onChange={(e) => handleResourceChange(index, e)}
                          rows="1"
                          className="input-field text-sm mt-1"
                          placeholder="Brief description"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveResource(index)}
                      className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200 self-center"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Save Changes
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
} 